import chroma from 'chroma-js';

/**
 * Color generation utilities for the Coolors-style generator.
 * Pure functions only — no React.
 */

export const GENERATION_MODES = [
  { id: 'auto', label: 'Auto' },
  { id: 'monochromatic', label: 'Monochromatic' },
  { id: 'analogous', label: 'Analogous' },
  { id: 'complementary', label: 'Complementary' },
  { id: 'split-complementary', label: 'Split Complementary' },
  { id: 'triadic', label: 'Triadic' },
  { id: 'tetradic', label: 'Tetradic' },
];

export const MIN_COLORS = 2;
export const MAX_COLORS = 10;

/**
 * A single aesthetically-weighted random hex.
 * Uses constrained HSL ranges so colors read as "designed" rather than noise.
 */
export function randomHex() {
  const h = Math.random() * 360;
  const s = 0.45 + Math.random() * 0.45; // 0.45 - 0.90
  const l = 0.35 + Math.random() * 0.4; // 0.35 - 0.75
  return chroma.hsl(h, s, l).hex();
}

function normalizeHue(h) {
  if (Number.isNaN(h) || h == null) return Math.random() * 360;
  return ((h % 360) + 360) % 360;
}

// Hue offsets (relative to a base hue) per color-theory mode.
const MODE_OFFSETS = {
  complementary: [0, 180],
  'split-complementary': [0, 150, 210],
  triadic: [0, 120, 240],
  tetradic: [0, 90, 180, 270],
};

function buildHues(mode, baseHue, count) {
  if (mode === 'analogous') {
    const spread = 30; // +/- 30 degrees around the base hue
    const start = baseHue - spread;
    return Array.from({ length: count }, (_, i) =>
      count === 1 ? baseHue : start + spread * 2 * (i / (count - 1))
    );
  }
  const offsets = MODE_OFFSETS[mode];
  if (offsets) {
    return Array.from({ length: count }, (_, i) => baseHue + offsets[i % offsets.length]);
  }
  // auto / fallback: independent random hues
  return Array.from({ length: count }, () => Math.random() * 360);
}

/**
 * Generate a new palette of hex strings for the given mode, preserving locked colors.
 * @param {string} mode - one of GENERATION_MODES ids
 * @param {Array<{hex:string, locked:boolean}>} currentColors
 * @returns {string[]} array of hex strings (same length as currentColors)
 */
export function generatePalette(mode, currentColors) {
  const count = currentColors.length;
  if (count === 0) return [];

  // Anchor the base hue/saturation on the first locked color when present.
  const lockedColor = currentColors.find(c => c.locked);
  let baseHue;
  let baseSat;
  if (lockedColor) {
    const c = chroma(lockedColor.hex);
    baseHue = normalizeHue(c.get('hsl.h'));
    baseSat = c.get('hsl.s');
  } else {
    baseHue = Math.random() * 360;
    baseSat = 0.5 + Math.random() * 0.3;
  }
  if (Number.isNaN(baseSat) || baseSat == null) baseSat = 0.55;

  let generated;
  if (mode === 'monochromatic') {
    // Same hue, lightness ramp from dark to light for a cohesive scale.
    generated = Array.from({ length: count }, (_, i) => {
      const t = count === 1 ? 0.5 : i / (count - 1);
      const l = 0.18 + 0.64 * t;
      return chroma.hsl(baseHue, baseSat, l).hex();
    });
  } else {
    const hues = buildHues(mode, baseHue, count);
    generated = hues.map(h => {
      const s = 0.45 + Math.random() * 0.45;
      const l = 0.3 + Math.random() * 0.45;
      return chroma.hsl(normalizeHue(h), s, l).hex();
    });
  }

  return currentColors.map((c, i) => (c.locked ? c.hex : generated[i]));
}

/**
 * Create a fresh palette of `count` colors using the given mode.
 */
export function createInitialPalette(count = 5, mode = 'auto') {
  const seed = Array.from({ length: count }, () => ({ hex: '#000000', locked: false }));
  return generatePalette(mode, seed).map(hex => ({ hex, locked: false }));
}

/**
 * Encode colors into a URL-friendly hex string: "EB3D30-1E8FEB-45A749".
 */
export function encodeHexString(colors) {
  return colors
    .map(c => c.hex.replace('#', '').toUpperCase())
    .join('-');
}

/**
 * Decode a URL hex string back into a colors array. Returns null when invalid.
 */
export function decodeHexString(str) {
  if (!str) return null;
  const parts = str.split('-').map(s => s.trim()).filter(Boolean);
  const colors = [];
  for (const part of parts) {
    const hex = `#${part}`;
    if (chroma.valid(hex)) {
      colors.push({ hex: chroma(hex).hex(), locked: false });
    }
  }
  return colors.length >= MIN_COLORS ? colors.slice(0, MAX_COLORS) : null;
}
